import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { api } from './api';
import { useAuth } from './auth';
import { useI18n } from './i18n';
import QuestionPlayer, { QuestionPlayerQuestion, SupportedLanguage } from './QuestionPlayer';
import { getRuntimeConfigValue } from './runtimeConfig';
import './ExamPage.css';

type ExamSessionResponse = {
  basic_questions: QuestionPlayerQuestion[];
  specialist_questions: QuestionPlayerQuestion[];
};

type ExamCheckResponse = {
  answers: Array<{
    question_id: string;
    question_number: number;
    correct: boolean;
    user_answer: AnswerValue;
    correct_answer: Exclude<AnswerValue, null>;
  }>;
  score: number;
  max_score: number;
  passed: boolean;
  attempt: {
    id: number;
    language: SupportedLanguage;
    category: string;
    score: number;
    max_score: number;
    passed: boolean;
    created_at: string;
    summary: ExamSummaryByScope;
  };
  summary: ExamSummaryByScope;
};

type ExamSummaryAnswerOption = {
  value: Exclude<AnswerValue, null>;
  label: string;
};

type ExamSummaryItem = {
  question_id: string;
  order: number;
  question_number: number;
  scope: 'PODSTAWOWY' | 'SPECJALISTYCZNY';
  type: 'TAK_NIE' | 'ABC';
  points: number;
  question_text: string;
  media_file: string | null;
  media_type: 'VIDEO' | 'IMAGE' | null;
  answer_options: ExamSummaryAnswerOption[];
  user_answer: AnswerValue;
  correct_answer: Exclude<AnswerValue, null>;
  correct: boolean;
};

type ExamSummaryByScope = {
  PODSTAWOWY: ExamSummaryItem[];
  SPECJALISTYCZNY: ExamSummaryItem[];
};

type TestHistoryItem = {
  id: number;
  language: SupportedLanguage;
  category: string;
  score: number;
  max_score: number;
  passed: boolean;
  created_at: string;
};

type TestHistoryDetail = {
  id: number;
  language: SupportedLanguage;
  category: string;
  score: number;
  max_score: number;
  passed: boolean;
  created_at: string;
  summary: ExamSummaryByScope;
};

type AnswerValue = 'T' | 'N' | 'A' | 'B' | 'C' | null;

const EXAM_DURATION_SECONDS = 25 * 60;
const PASS_SCORE = 68;

export default function ExamPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, isLoading, user } = useAuth();
  const { language, t } = useI18n();
  const [questions, setQuestions] = useState<QuestionPlayerQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isStarting, setIsStarting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION_SECONDS);
  const [examStarted, setExamStarted] = useState(false);
  const [activeExamLanguage, setActiveExamLanguage] = useState<SupportedLanguage>(language);
  const [result, setResult] = useState<ExamCheckResponse | null>(null);
  const [pendingResult, setPendingResult] = useState<ExamCheckResponse | null>(null);
  const [timeExpired, setTimeExpired] = useState(false);
  const [showResultAdGate, setShowResultAdGate] = useState(false);
  const [resultAdSecondsLeft, setResultAdSecondsLeft] = useState(5);
  const [testHistory, setTestHistory] = useState<TestHistoryItem[]>([]);
  const [selectedHistoryAttempt, setSelectedHistoryAttempt] = useState<TestHistoryDetail | null>(null);
  const [expandedSummaryQuestionId, setExpandedSummaryQuestionId] = useState<string | null>(null);
  const [historyError, setHistoryError] = useState('');
  const submittingRef = useRef(false);
  const selectedAttemptId = searchParams.get('attempt');

  const copy = t.exam;
  const currentQuestion = questions[currentIndex] ?? null;
  const answeredCount = useMemo(
    () => Object.values(answers).filter((value) => value !== null).length,
    [answers],
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setTestHistory([]);
      setSelectedHistoryAttempt(null);
      return;
    }
    void loadTestHistory();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (!selectedAttemptId) {
      setSelectedHistoryAttempt(null);
      return;
    }

    async function loadAttemptDetails() {
      try {
        const response = await api.get<TestHistoryDetail>(`/study/test-history/${selectedAttemptId}/`);
        setSelectedHistoryAttempt(response.data);
        setFetchError('');
        setResult(null);
        setPendingResult(null);
        setExamStarted(false);
      } catch (error: any) {
        const apiError = error?.response?.data?.error;
        setFetchError(typeof apiError === 'string' ? apiError : copy.fetchErrorFallback);
        setSelectedHistoryAttempt(null);
      }
    }

    void loadAttemptDetails();
  }, [copy.fetchErrorFallback, isAuthenticated, selectedAttemptId]);

  async function loadTestHistory() {
    if (!isAuthenticated) {
      return;
    }

    try {
      const response = await api.get<{ results: TestHistoryItem[] }>('/study/test-history/');
      setTestHistory(response.data.results);
      setHistoryError('');
    } catch (error) {
      setHistoryError(copy.fetchErrorFallback);
    }
  }

  useEffect(() => {
    if (!examStarted || result || timeLeft <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setTimeLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [examStarted, result, timeLeft]);

  useEffect(() => {
    if (!examStarted || result || timeLeft > 0 || submittingRef.current) {
      return;
    }

    setTimeExpired(true);
    void submitExam();
  }, [examStarted, result, timeLeft]);

  useEffect(() => {
    if (!showResultAdGate || resultAdSecondsLeft <= 0) {
      return;
    }

    const timer = window.setInterval(() => {
      setResultAdSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(timer);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(timer);
  }, [showResultAdGate, resultAdSecondsLeft]);

  async function startExam() {
    if (!isAuthenticated || !user?.preferred_exam_category) {
      return;
    }

    setIsStarting(true);
    setFetchError('');
    setResult(null);
    setPendingResult(null);
    setSelectedHistoryAttempt(null);
    setTimeExpired(false);
    setShowResultAdGate(false);
    setResultAdSecondsLeft(5);
    if (selectedAttemptId) {
      setSearchParams({});
    }

    try {
      const sessionLanguage = language;
      setActiveExamLanguage(sessionLanguage);
      const response = await api.get<ExamSessionResponse>('/study/exam-session/', {
        params: { language: sessionLanguage },
      });
      const orderedQuestions = [...response.data.basic_questions, ...response.data.specialist_questions];
      setQuestions(orderedQuestions);
      setAnswers(
        orderedQuestions.reduce<Record<string, AnswerValue>>((acc, question) => {
          acc[question.id] = null;
          return acc;
        }, {}),
      );
      setCurrentIndex(0);
      setExpandedSummaryQuestionId(null);
      setTimeLeft(EXAM_DURATION_SECONDS);
      setExamStarted(true);
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      setFetchError(typeof apiError === 'string' ? apiError : copy.fetchErrorFallback);
      setExamStarted(false);
      setQuestions([]);
      setAnswers({});
    } finally {
      setIsStarting(false);
    }
  }

  async function submitExam() {
    if (!questions.length || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setFetchError('');

    try {
      const response = await api.post<ExamCheckResponse>('/study/check-exam/', {
        language: activeExamLanguage,
        answers: questions.map((question) => ({
          question_id: question.id,
          answer: answers[question.id] ?? null,
        })),
      });
      await loadTestHistory();
      if (user?.account_tier === 'FREE') {
        setPendingResult(response.data);
        setShowResultAdGate(true);
        setResultAdSecondsLeft(5);
      } else {
        setResult(response.data);
      }
      setExpandedSummaryQuestionId(null);
      setSelectedHistoryAttempt(null);
      setExamStarted(false);
    } catch (error: any) {
      const apiError = error?.response?.data?.error;
      setFetchError(typeof apiError === 'string' ? apiError : copy.submitErrorFallback);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  function handleRevealResult() {
    if (!pendingResult || resultAdSecondsLeft > 0) {
      return;
    }

    setResult(pendingResult);
    setPendingResult(null);
    setShowResultAdGate(false);
  }

  function handleHistoryOpen(attemptId: number) {
    setSearchParams({ attempt: String(attemptId) });
  }

  function handleLeaveHistoryResult() {
    setSelectedHistoryAttempt(null);
    setExpandedSummaryQuestionId(null);
    setSearchParams({});
  }

  function handleSummaryQuestionToggle(questionId: string) {
    setExpandedSummaryQuestionId((current) => (current === questionId ? null : questionId));
  }

  function handleAnswerSelect(answer: 'T' | 'N' | 'A' | 'B' | 'C') {
    if (!currentQuestion || result) {
      return;
    }

    setAnswers((current) => ({
      ...current,
      [currentQuestion.id]: answer,
    }));
  }

  function handleQuestionTimeout() {
    if (!currentQuestion || result || isSubmitting) {
      return;
    }

    const isLastQuestion = currentIndex >= questions.length - 1;
    if (isLastQuestion) {
      void submitExam();
      return;
    }

    setCurrentIndex((current) => current + 1);
  }

  function handleNextQuestion() {
    if (!currentQuestion || result) {
      return;
    }

    const isLastQuestion = currentIndex >= questions.length - 1;
    if (isLastQuestion) {
      void submitExam();
      return;
    }

    setCurrentIndex((current) => current + 1);
  }

  function getAnswerLabel(item: ExamSummaryItem, value: AnswerValue) {
    if (!value) {
      return copy.unanswered;
    }

    const option = item.answer_options.find((answerOption) => answerOption.value === value);
    return option ? `${value}. ${option.label}` : value;
  }

  function renderSummaryDetail(item: ExamSummaryItem) {
    const mediaUrl = item.media_file
      ? `${(getRuntimeConfigValue('REACT_APP_MEDIA_BASE_URL') || 'https://pub-2cdeeb37268f46c7b8d1013e3ab56448.r2.dev').replace(/\/+$/, '')}/videos/${item.media_file.replace(/\.wmv$/i, '.mp4')}`
      : '';

    return (
      <article
        className={`exam-page__summary-detail ${
          item.correct ? 'exam-page__summary-detail--correct' : 'exam-page__summary-detail--wrong'
        }`}
      >
        <div className="exam-page__summary-detail-head">
          <div className="exam-page__summary-badges">
            <span className="exam-page__summary-badge">
              {copy.questionCounter} {item.order}
            </span>
            <span className="exam-page__summary-badge exam-page__summary-badge--scope">
              {item.scope === 'PODSTAWOWY' ? copy.basicQuestionsTitle : copy.specialistQuestionsTitle}
            </span>
            <span className="exam-page__summary-badge exam-page__summary-badge--points">
              {item.points} pkt
            </span>
          </div>
          <span className={`exam-page__summary-state ${item.correct ? 'exam-page__summary-state--correct' : 'exam-page__summary-state--wrong'}`}>
            {item.correct ? copy.correctAnswer : copy.wrongAnswer}
          </span>
        </div>

        {mediaUrl ? (
          <div className="exam-page__summary-media-shell">
            {item.media_type === 'VIDEO' ? (
              <video className="exam-page__summary-media" controls preload="metadata" src={mediaUrl} playsInline />
            ) : (
              <img className="exam-page__summary-media" src={mediaUrl} alt={item.question_text} />
            )}
          </div>
        ) : null}

        <h3 className="exam-page__summary-question">{item.question_text}</h3>

        <div className={`exam-page__summary-options ${item.type === 'TAK_NIE' ? 'exam-page__summary-options--yes-no' : ''}`}>
          {item.answer_options.map((option) => {
            const isUserAnswer = item.user_answer === option.value;
            const isCorrectAnswer = item.correct_answer === option.value;
            return (
              <div
                key={option.value}
                className={`exam-page__summary-option ${
                  isCorrectAnswer ? 'exam-page__summary-option--correct' : ''
                } ${isUserAnswer && !isCorrectAnswer ? 'exam-page__summary-option--wrong' : ''} ${
                  isUserAnswer ? 'exam-page__summary-option--selected' : ''
                }`}
              >
                <span>{option.value}.</span>
                <span>{option.label}</span>
              </div>
            );
          })}
        </div>

        <div className="exam-page__summary-answer-row">
          <span>{copy.userAnswer}: {getAnswerLabel(item, item.user_answer)}</span>
          {!item.correct ? <span>{copy.expectedAnswer}: {getAnswerLabel(item, item.correct_answer)}</span> : null}
        </div>
      </article>
    );
  }

  function renderSummarySection(title: string, items: ExamSummaryItem[]) {
    if (!items.length) {
      return null;
    }

    const expandedItem = items.find((item) => item.question_id === expandedSummaryQuestionId) ?? null;

    return (
      <div className="exam-page__summary-section">
        <h3>{title}</h3>
        <div className="exam-page__summary-grid">
          {items.map((item) => (
            <button
              key={item.question_id}
              type="button"
              onClick={() => handleSummaryQuestionToggle(item.question_id)}
              className={`exam-page__summary-tile ${
                item.correct ? 'exam-page__summary-tile--correct' : 'exam-page__summary-tile--wrong'
              } ${expandedSummaryQuestionId === item.question_id ? 'exam-page__summary-tile--active' : ''}`}
            >
              {item.order}
            </button>
          ))}
        </div>
        {expandedItem ? renderSummaryDetail(expandedItem) : null}
      </div>
    );
  }

  function renderExamSummary(summary: ExamSummaryByScope) {
    return (
      <section className="exam-page__panel exam-page__summary">
        <h2>{copy.summaryTitle}</h2>
        {renderSummarySection(copy.basicQuestionsTitle, summary.PODSTAWOWY)}
        {renderSummarySection(copy.specialistQuestionsTitle, summary.SPECJALISTYCZNY)}
      </section>
    );
  }

  const canMoveNext = !!currentQuestion && (answers[currentQuestion.id] !== null || isSubmitting);
  const isPassed = (result?.score ?? 0) >= PASS_SCORE;
  const displayedResult = result ?? selectedHistoryAttempt;
  const displayedIsPassed = displayedResult?.passed ?? false;
  const displayedAnsweredCount = displayedResult
    ? [...displayedResult.summary.PODSTAWOWY, ...displayedResult.summary.SPECJALISTYCZNY].filter(
        (item) => item.user_answer !== null,
      ).length
    : 0;
  const displayedQuestionCount = displayedResult
    ? displayedResult.summary.PODSTAWOWY.length + displayedResult.summary.SPECJALISTYCZNY.length
    : 0;

  return (
    <div className="exam-page">
      <div className="exam-page__shell">
        <section className="exam-page__panel exam-page__intro">
          <h1 className="exam-page__title">{copy.title}</h1>
          <p className="exam-page__description">{copy.description}</p>
        </section>

        {isLoading ? (
          <section className="exam-page__panel exam-page__message">{copy.loadingSession}</section>
        ) : !isAuthenticated ? (
          <section className="exam-page__panel exam-page__message">{copy.loginRequired}</section>
        ) : (
          <>
            <section className="exam-page__panel exam-page__toolbar">
              <button
                type="button"
                className="exam-page__button exam-page__button--success"
                onClick={() => void startExam()}
                disabled={isStarting || examStarted || isSubmitting || !user?.preferred_exam_category}
              >
                {isStarting ? copy.startingExam : copy.startExam}
              </button>
            </section>

            {fetchError ? <section className="exam-page__panel exam-page__error">{fetchError}</section> : null}

            {displayedResult ? (
              <>
                <section className="exam-page__panel exam-page__result">
                  <h2>{copy.resultTitle}</h2>
                  <div
                    className={`exam-page__result-badge ${
                      displayedIsPassed ? 'exam-page__result-badge--pass' : 'exam-page__result-badge--fail'
                    }`}
                  >
                    {displayedIsPassed ? copy.passLabel : copy.failLabel}
                  </div>

                  <div className="exam-page__result-grid">
                    <div className="exam-page__stat">
                      <div className="exam-page__stat-label">{copy.scoreLabel}</div>
                      <div className="exam-page__stat-value">{displayedResult.score}</div>
                    </div>
                    <div className="exam-page__stat">
                      <div className="exam-page__stat-label">{copy.maxScoreLabel}</div>
                      <div className="exam-page__stat-value">{displayedResult.max_score}</div>
                    </div>
                    <div className="exam-page__stat">
                      <div className="exam-page__stat-label">{copy.answeredCount}</div>
                      <div className="exam-page__stat-value">{displayedAnsweredCount} / {displayedQuestionCount}</div>
                    </div>
                  </div>

                  <p className="exam-page__description" style={{ marginTop: 18 }}>
                    {copy.passRule}
                  </p>
                  {selectedHistoryAttempt ? (
                    <div className="exam-page__footer">
                      <button
                        type="button"
                        className="exam-page__button exam-page__button--secondary"
                        onClick={handleLeaveHistoryResult}
                      >
                        {copy.testHistoryTitle}
                      </button>
                    </div>
                  ) : null}
                  {timeExpired ? (
                    <p className="exam-page__description" style={{ marginTop: 12 }}>
                      {copy.globalTime}: 0:00
                    </p>
                  ) : null}
                </section>
                {renderExamSummary(displayedResult.summary)}
              </>
            ) : examStarted && currentQuestion ? (
              <>
                <div className="content-layout">
                  <QuestionPlayer
                    key={currentQuestion.id}
                    question={currentQuestion}
                    language={activeExamLanguage}
                    questionOrder={currentIndex + 1}
                    totalQuestions={questions.length}
                    answeredCount={answeredCount}
                    remainingCount={questions.length - currentIndex - 1}
                    examTimeLeft={timeLeft}
                    examTimeLabel={copy.globalTime}
                    answeredLabel={copy.answeredCount}
                    remainingLabel={copy.remainingCount}
                    nextButtonLabel={
                      currentIndex >= questions.length - 1
                        ? isSubmitting
                          ? copy.submitInProgress
                          : copy.finishExam
                        : copy.nextQuestion
                    }
                    nextButtonDisabled={!canMoveNext || isSubmitting}
                    onNextQuestion={handleNextQuestion}
                    onAnswerSelect={handleAnswerSelect}
                    onTimeExpired={handleQuestionTimeout}
                  />
                </div>
              </>
            ) : (
              <section className="exam-page__panel exam-page__message">{copy.noExamLoaded}</section>
            )}

            {!examStarted && !displayedResult ? (
              <section className="exam-page__panel exam-page__history">
                <h2>{copy.testHistoryTitle}</h2>
                {historyError ? <p className="exam-page__error">{historyError}</p> : null}
                {!historyError && testHistory.length === 0 ? (
                  <p className="exam-page__description">{copy.noTestHistory}</p>
                ) : null}
                {testHistory.length > 0 ? (
                  <div className="exam-page__history-list">
                    {testHistory.map((attempt) => (
                      <button
                        key={attempt.id}
                        type="button"
                        className="exam-page__history-item"
                        onClick={() => handleHistoryOpen(attempt.id)}
                      >
                        <span>{new Date(attempt.created_at).toLocaleString()}</span>
                        <strong>
                          {attempt.score}/{attempt.max_score}
                        </strong>
                        <span>{attempt.passed ? copy.passLabel : copy.failLabel}</span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            {showResultAdGate && pendingResult ? (
              <div className="exam-page__modal-backdrop">
                <div className="exam-page__modal">
                  <h2 className="exam-page__modal-title">{copy.resultAdTitle}</h2>
                  <p className="exam-page__description">{copy.resultAdText}</p>
                  <div className="exam-page__modal-footer">
                    <a href="/" className="exam-page__button exam-page__button--success exam-page__button-link">
                      {copy.resultAdPremiumCta}
                    </a>
                    <button
                      type="button"
                      className="exam-page__button exam-page__button--secondary"
                      onClick={handleRevealResult}
                      disabled={resultAdSecondsLeft > 0}
                    >
                      {resultAdSecondsLeft > 0
                        ? `${copy.resultAdCountdown} ${resultAdSecondsLeft}s`
                        : copy.resultAdContinue}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
